import { useState, useMemo, useRef } from "react";
import {
  Search, Plus, Pencil, Trash2, Minus, ImageIcon, Package,
  Upload, Download, Loader2, X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { resolveImageUrl } from "@/lib/apiClient";
import { exportCsv } from "@/lib/storage";
import { useFields } from "@/context/FieldsContext";
import { resolvedEnabledFields, makeField } from "@/lib/fields";

const catColor = (cat = "") => {
  const map = {
    Electronics: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    Tools: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    "Power Tools": "bg-amber-500/15 text-amber-300 border-amber-500/30",
    Hardware: "bg-slate-500/15 text-slate-300 border-slate-500/30",
    Furniture: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    Office: "bg-purple-500/15 text-purple-300 border-purple-500/30",
    Kitchen: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    Apparel: "bg-pink-500/15 text-pink-300 border-pink-500/30",
  };
  return map[cat] || "bg-primary/10 text-primary border-primary/25";
};

const emptyItem = { item_name: "", description: "", category: "Other", quantity: 1, fields: {} };

export const SpreadsheetTable = ({ items, onAdd, onUpdate, onDelete, onImageClick, onBulkDelete, onImport }) => {
  const { config, setConfig } = useFields();
  const criteria = resolvedEnabledFields(config);
  const [editingHeaderId, setEditingHeaderId] = useState(null);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyItem);
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [importing, setImporting] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const importInputRef = useRef(null);

  const categories = useMemo(
    () => Array.from(new Set(items.map((i) => i.category).filter(Boolean))).sort(),
    [items]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      const matchCat = cat === "all" || i.category === cat;
      const matchQ =
        !q ||
        [i.item_name, i.description, i.category, i.extracted_text, i.id, ...Object.values(i.fields || {})]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q));
      return matchCat && matchQ;
    });
  }, [items, search, cat]);

  const openEdit = (item) => {
    setEditing(item);
    setDraft({ ...item, fields: { ...(item.fields || {}) } });
  };

  const saveEdit = () => {
    onUpdate(editing.id, {
      item_name: draft.item_name,
      description: draft.description,
      category: draft.category || "Other",
      quantity: Number(draft.quantity) || 0,
      fields: draft.fields || {},
    });
    setEditing(null);
  };

  const saveAdd = () => {
    if (!draft.item_name.trim()) return;
    onAdd({ ...draft, quantity: Number(draft.quantity) || 1, fields: draft.fields || {} });
    setAddOpen(false);
    setDraft(emptyItem);
  };

  const renameHeader = (fieldId, label) =>
    setConfig((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.id === fieldId ? { ...f, label } : f)),
    }));

  const addInlineField = () =>
    setConfig((prev) => ({ ...prev, fields: [...prev.fields, makeField()] }));

  const changeQty = (item, delta) => {
    const q = Math.max(0, (Number(item.quantity) || 0) + delta);
    onUpdate(item.id, { quantity: q });
  };

  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const allSelected = filtered.length > 0 && filtered.every((i) => selected.has(i.id));
  const toggleAll = () =>
    setSelected((prev) => {
      if (allSelected) {
        const next = new Set(prev);
        filtered.forEach((i) => next.delete(i.id));
        return next;
      }
      return new Set([...prev, ...filtered.map((i) => i.id)]);
    });

  const selectedItems = items.filter((i) => selected.has(i.id));

  const exportSelected = () => {
    exportCsv(selectedItems, config.fields);
  };

  const confirmBulkDelete = async () => {
    await onBulkDelete(Array.from(selected));
    setSelected(new Set());
    setBulkConfirm(false);
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      await onImport(file);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b border-border">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items, descriptions, serials…"
            className="pl-9 bg-secondary/40 border-border"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger
              data-testid="category-filter-select"
              className="w-[150px] bg-secondary/40 border-border"
            >
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="import-button"
            onClick={() => importInputRef.current?.click()}
            disabled={importing}
            variant="outline"
            className="gap-2 border-border bg-secondary/40 hover:bg-secondary whitespace-nowrap"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            <span className="hidden sm:inline">Import</span>
          </Button>
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            data-testid="import-file-input"
            onChange={(e) => { handleImportFile(e.target.files?.[0]); e.target.value = ""; }}
          />
          <Button
            data-testid="add-item-button"
            onClick={() => { setDraft(emptyItem); setAddOpen(true); }}
            variant="outline"
            className="gap-2 border-border bg-secondary/40 hover:bg-secondary whitespace-nowrap"
          >
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="flex items-center justify-between gap-3 px-4 py-3 bg-primary/10 border-b border-primary/20 fade-up">
          <div className="flex items-center gap-2 text-sm text-slate-100">
            <Badge className="bg-primary text-primary-foreground" data-testid="bulk-selected-count">{selected.size}</Badge>
            item{selected.size === 1 ? "" : "s"} selected
            <button
              onClick={() => setSelected(new Set())}
              className="ml-1 text-xs text-muted-foreground hover:text-slate-200 inline-flex items-center gap-1"
            >
              <X className="h-3 w-3" /> clear
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="bulk-export-button"
              onClick={exportSelected}
              variant="outline"
              size="sm"
              className="gap-2 border-border bg-card hover:bg-secondary"
            >
              <Download className="h-4 w-4" /> Export selected
            </Button>
            <Button
              data-testid="bulk-delete-button"
              onClick={() => setBulkConfirm(true)}
              size="sm"
              className="gap-2 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trash2 className="h-4 w-4" /> Delete selected
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table data-testid="inventory-table" className="w-full text-left">
          <thead>
            <tr className="border-b border-border bg-secondary/30 text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 w-10">
                <Checkbox
                  data-testid="select-all-checkbox"
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
              <th className="px-4 py-3 font-semibold">ID</th>
              <th className="px-4 py-3 font-semibold">Timestamp</th>
              <th className="px-4 py-3 font-semibold">Item</th>
              <th className="px-4 py-3 font-semibold hidden md:table-cell">Description / Attributes</th>
              <th className="px-4 py-3 font-semibold">Category</th>
              {criteria.map((f) => (
                <th key={f.id} className="px-4 py-3 font-semibold" data-testid="custom-field-header">
                  {editingHeaderId === f.id ? (
                    <input
                      data-testid="custom-field-header-input"
                      autoFocus
                      defaultValue={f.label}
                      onBlur={(e) => { renameHeader(f.id, e.target.value); setEditingHeaderId(null); }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { renameHeader(f.id, e.target.value); setEditingHeaderId(null); }
                        if (e.key === "Escape") setEditingHeaderId(null);
                      }}
                      className="w-28 rounded bg-card border border-primary/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-100 outline-none"
                    />
                  ) : (
                    <button
                      data-testid="custom-field-header-label"
                      onClick={() => setEditingHeaderId(f.id)}
                      title="Click to rename"
                      className="inline-flex items-center gap-1 hover:text-primary transition-colors group/h"
                    >
                      {f.resolvedLabel}
                      <Pencil className="h-2.5 w-2.5 opacity-0 group-hover/h:opacity-60" />
                    </button>
                  )}
                </th>
              ))}
              <th className="px-4 py-3 font-semibold text-center">Qty</th>
              <th className="px-4 py-3 font-semibold hidden sm:table-cell">Image</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9 + criteria.length} className="px-4 py-16 text-center">
                  <Package className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {items.length === 0 ? "No items yet. Scan or add your first item." : "No items match your search."}
                  </p>
                </td>
              </tr>
            )}
            {filtered.map((item) => {
              const img = resolveImageUrl(item.source_image_url);
              return (
                <tr
                  key={item.id}
                  data-testid="table-row-item"
                  className={`border-b border-border/60 hover:bg-[#1f2937]/60 transition-colors group ${selected.has(item.id) ? "bg-primary/5" : ""}`}
                >
                  <td className="px-4 py-3">
                    <Checkbox
                      data-testid="row-select-checkbox"
                      checked={selected.has(item.id)}
                      onCheckedChange={() => toggleOne(item.id)}
                      aria-label="Select row"
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-primary/80 whitespace-nowrap">
                    {item.id.slice(0, 8)}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(item.timestamp).toLocaleDateString()}{" "}
                    <span className="opacity-60">
                      {new Date(item.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-sm text-slate-100">{item.item_name}</div>
                    {item.extracted_text && (
                      <div className="font-mono text-[10px] text-sky-400/80 mt-0.5 max-w-[200px] truncate">
                        {item.extracted_text}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-300 max-w-[280px] hidden md:table-cell">
                    <div className="line-clamp-2">{item.description || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={`text-[10px] font-medium ${catColor(item.category)}`}>
                      {item.category}
                    </Badge>
                  </td>
                  {criteria.map((f) => (
                    <td key={f.id} className="px-4 py-3 text-xs text-slate-300 max-w-[180px]" data-testid="custom-field-cell">
                      {item.fields && item.fields[f.id] ? (
                        <span className="line-clamp-2">{item.fields[f.id]}</span>
                      ) : (
                        <span className="text-muted-foreground/40">—</span>
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        data-testid="item-qty-decrement"
                        onClick={() => changeQty(item, -1)}
                        className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-slate-100"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="w-8 text-center font-mono text-sm tabular-nums text-slate-100">
                        {item.quantity}
                      </span>
                      <button
                        data-testid="item-qty-increment"
                        onClick={() => changeQty(item, 1)}
                        className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-slate-100"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {img ? (
                      <button
                        data-testid="row-image-thumb"
                        onClick={() => onImageClick(item)}
                        className="h-10 w-10 overflow-hidden rounded-lg border border-border hover:ring-2 hover:ring-primary/50 transition-all"
                      >
                        <img src={img} alt={item.item_name} className="h-full w-full object-cover" />
                      </button>
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground/40">
                        <ImageIcon className="h-4 w-4" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid="edit-item-button"
                        onClick={() => openEdit(item)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-primary/15 hover:text-primary transition-colors"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        data-testid="delete-item-button"
                        onClick={() => onDelete(item.id)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/15 hover:text-destructive transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit / Add dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-item-dialog" className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-50">Edit item</DialogTitle>
            <DialogDescription className="sr-only">Update the item details</DialogDescription>
          </DialogHeader>
          <ItemForm draft={draft} setDraft={setDraft} criteria={criteria} onAddField={addInlineField} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="save-edit-button" onClick={saveEdit} className="bg-primary text-primary-foreground hover:bg-primary/90">
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-item-dialog" className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-50">Add item manually</DialogTitle>
            <DialogDescription className="sr-only">Create a new inventory item</DialogDescription>
          </DialogHeader>
          <ItemForm draft={draft} setDraft={setDraft} criteria={criteria} onAddField={addInlineField} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid="save-add-button" onClick={saveAdd} className="bg-primary text-primary-foreground hover:bg-primary/90">
              Add to inventory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent data-testid="bulk-delete-confirm-modal" className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-slate-50">
              Delete {selected.size} selected item{selected.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              This permanently removes the selected items from your inventory. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="ghost" onClick={() => setBulkConfirm(false)}>Cancel</Button>
            <Button
              data-testid="bulk-delete-confirm-button"
              onClick={confirmBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete {selected.size}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ItemForm = ({ draft, setDraft, criteria = [], onAddField }) => (
  <div className="space-y-3">
    <div>
      <Label className="text-xs text-muted-foreground">Item Name</Label>
      <Input
        data-testid="form-item-name"
        value={draft.item_name}
        onChange={(e) => setDraft({ ...draft, item_name: e.target.value })}
        className="bg-secondary/40 border-border mt-1"
        placeholder="e.g. DeWalt Cordless Drill"
      />
    </div>
    <div>
      <Label className="text-xs text-muted-foreground">Description / Attributes</Label>
      <Textarea
        data-testid="form-description"
        value={draft.description}
        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        className="bg-secondary/40 border-border mt-1 resize-none"
        rows={2}
      />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-xs text-muted-foreground">Category</Label>
        <Input
          data-testid="form-category"
          value={draft.category}
          onChange={(e) => setDraft({ ...draft, category: e.target.value })}
          className="bg-secondary/40 border-border mt-1"
        />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Quantity</Label>
        <Input
          data-testid="form-quantity"
          type="number"
          min={0}
          value={draft.quantity}
          onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
          className="bg-secondary/40 border-border mt-1"
        />
      </div>
    </div>
    {criteria.length > 0 && (
      <div className="grid grid-cols-2 gap-3 pt-1">
        {criteria.map((f) => (
          <div key={f.id}>
            <Label className="text-xs text-muted-foreground">{f.resolvedLabel}</Label>
            <Input
              data-testid="form-custom-field"
              value={(draft.fields && draft.fields[f.id]) || ""}
              onChange={(e) =>
                setDraft({ ...draft, fields: { ...(draft.fields || {}), [f.id]: e.target.value } })
              }
              className="bg-secondary/40 border-border mt-1"
              placeholder={f.ai_instruction || ""}
            />
          </div>
        ))}
      </div>
    )}
    {onAddField && (
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="form-add-field-button"
        onClick={onAddField}
        className="w-full gap-1.5 border-dashed border-border bg-secondary/20 hover:bg-secondary/40 text-muted-foreground hover:text-slate-100"
      >
        <Plus className="h-3.5 w-3.5" /> Add field
      </Button>
    )}
  </div>
);
